import { Role, WorkOrderSortColumnEnum, WorkOrderSortDirectionEnum, WorkOrderSummaryDto } from '@inosio/payments-sdk';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Grid,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkOrders, selectWorkOrderSearchFitlers, selectWorkOrderState, setWorkOrderSearchFilters } from './sagas/workorderSlice';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../icons/Badrequesterror.svg';
import Error500 from '../../icons/505Error.svg';
import { format } from 'date-fns';
import ReviewOtpIcon from '../../icons/ReviewOtpIcon.svg';
import LetterAvatar from '../../components/LetterAvatar';
import { getPmAssociatedStaff, selectPmConfigSettingsState } from '../configSettings/sagas/pmConfigSettingsSlice';
import { useHistory } from 'react-router';
import CreateWorkOrderModal from './components/CreateWorkOrderModal';
import { selectCurrentUser } from '../../shared/sagas/auth/authSlice';
import { selectVendorsState } from '../vendors/sagas/vendorsSlice';
import useMediaQuery from '@mui/material/useMediaQuery';
import WorkOrdersTableHeader from './components/workordersTableHeader/WorkOrdersTableHeader';

export default function WorkOrders() {
  const useStyles = makeStyles({
    gridContainer: {
      background: '#FFFFFF',
      borderRadius: '24px',
    },
    dialog: {
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttontext: {
      fontFamily: 'sans-serif',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 'small',
    },
    dialogbox: {
      borderRadius: '20',
      borderStyle: 'solid',
    },
    avatarGroupRoot: {
      marginLeft: '14px',
      '& .MuiAvatar-root': {
        width: 24,
        height: 24,
        fontFamily: 'Work Sans !important',
        fontStyle: 'normal !important',
        fontWeight: '400 !important',
        fontSize: '10px !important',
        lightHeight: '18px !important',
        marginLeft: '-12px !important',
      },
    },
    avatarRoot: {
      '&.MuiAvatar-root': {
        width: 24,
        height: 24,
        fontFamily: 'Work Sans !important',
        fontStyle: 'normal !important',
        fontWeight: '400 !important',
        fontSize: '10px !important',
        lightHeight: '18px !important',
        marginLeft: '-12px !important',
      },
    },
  });
  const classes = useStyles();
  const matchesMobile = useMediaQuery('(max-width:420px)');
  const matchesIpad = useMediaQuery('(max-width:768px)');

  const history = useHistory();
  const dispatch = useDispatch();

  //selectors
  const workOrderSearchFitlers = useSelector(selectWorkOrderSearchFitlers)
  const {searchDateRange, recordStartIndex, rangeSize, searchValue, searchField, currentPage, selectedStatusOptions, assigneeUuids, sort, Title, WorkOrderId, PropertyName, ResidentName} = workOrderSearchFitlers
  const currentUser = useSelector(selectCurrentUser);
  const { associatedCompanyStaff } = useSelector(selectPmConfigSettingsState);
  const { Vendors } = useSelector(selectVendorsState);
  const workOrderState = useSelector(selectWorkOrderState);
  const { isFetchingWorkOrders, WorkOrdersDataPages, WorkOrdersFetchError, WorkOrdersFetchResponseDto, isFetchingWorkOrdersFailed, isCreatingWorkOrderSuccess } = workOrderState;

  const isVendorUser = currentUser?.currentProfile.role === Role.Vendor; 

  //local states
  const [globalError, setGlobalError] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
 //Handling selected workorder id from url: start
  const [openCreateWorkOrderModal, setOpenCreateWorkOrderModal] = useState(false);

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!WorkOrdersDataPages[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      dispatch(setWorkOrderSearchFilters({
        recordStartIndex: newIndex
      }))
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    dispatch(setWorkOrderSearchFilters({
      currentPage: newPageNumber
    }))
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setWorkOrderSearchFilters({
      currentPage: 0,
      recordStartIndex:0,
      rangeSize: parseInt(event.target.value)
    }))
  };

  const handleSearchesV2 = () => {
    const workOrdersSearchDto = {
      EndDate: searchDateRange?.[1] || undefined,
      StartDate: searchDateRange?.[0] || undefined,
      RangeSize: rangeSize,
      StartIndex: recordStartIndex,
      SearchField: searchField || undefined,
      SearchValue: searchValue || undefined,
      AssigneeUuids: assigneeUuids,
      Statuses: selectedStatusOptions,
      Sort: sort,
      Title: Title || undefined,
      WorkOrderId : WorkOrderId || undefined,
      PropertyName: PropertyName || undefined,
      ResidentName: ResidentName || undefined
    };

    dispatch(getWorkOrders(workOrdersSearchDto));
  };

  const openCreateModal = () => {
    setOpenCreateWorkOrderModal(true);
  };

  const handleClickWorkOrder = (workorder: WorkOrderSummaryDto) => {
    history.push(`/pmui/workorders/${workorder.WorkOrderId}`);
  };

  const handleClickTableRow = (workorder: WorkOrderSummaryDto) => {
    if (!matchesIpad) return;
    handleClickWorkOrder(workorder);
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  useEffect(() => {
    //we fetch only if we need to (ie, we haven't fetched yet and we aren't actively fetching)
    if (!associatedCompanyStaff.isCompleted && !associatedCompanyStaff.isFetching && !isVendorUser) {
      dispatch(getPmAssociatedStaff());
    }
  }, [dispatch]);

  useEffect(() => {
    handleSearchesV2();
  }, [dispatch, searchField, searchValue, searchDateRange, recordStartIndex, rangeSize, assigneeUuids, selectedStatusOptions, isCreatingWorkOrderSuccess, sort, Title, WorkOrderId, PropertyName, ResidentName]);

  useEffect(() => {
    setGlobalError('');
    if (isFetchingWorkOrdersFailed) {
      setSnackBarOpen(true);
      setGlobalError('Something went wrong. Please try again later.');
    }
  }, [isFetchingWorkOrdersFailed]);

  useEffect(() => {
    if (currentUser?.currentProfile.uuid && currentUser?.currentProfile.role === Role.MaintenanceStaff) {
      dispatch(setWorkOrderSearchFilters({
        assigneeUuids: [...workOrderSearchFitlers.assigneeUuids, currentUser.currentProfile.uuid]
      }))
    }
  }, [dispatch,currentUser]);


  const handleSort = (column: WorkOrderSortColumnEnum) => {
    if (sort.Column === column) {
      // If the same column is clicked, toggle the direction
      dispatch(setWorkOrderSearchFilters({
        recordStartIndex: 0,
        currentPage: 0,
        sort: {
            ...sort,
           Direction :sort.Direction === WorkOrderSortDirectionEnum.Asc ? WorkOrderSortDirectionEnum.Desc : WorkOrderSortDirectionEnum.Asc
        }
      }))
    } else {
      // If a new column is clicked, set it as the new sorting column and default to ascending
     dispatch(setWorkOrderSearchFilters({
      recordStartIndex: 0,
      currentPage: 0,
      sort: {
        Column : column,
        Direction : WorkOrderSortDirectionEnum.Desc
      },
    
     }))
    }
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc( 100vh - 144px )',
          pb: '64px',
        }}
        className={classes.gridContainer}
        style={{ position: 'relative' }}
      >
        <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
          <Alert onClose={handleAlertClose} severity="error">
            {globalError}
          </Alert>
        </Snackbar>
        <Fragment>
          <Box sx={{ display: 'flex', p: 0, pb: 1, justifyContent: 'space-between', '& .MuiTextField-root': { m: 1, width: '25ch' }, flexDirection: matchesMobile ? 'column-reverse' : 'row' }}>
          <WorkOrdersTableHeader />
            <Box sx={{ display: matchesIpad ? 'block' : 'flex' }}>
            </Box>
            {!isVendorUser && (
              <Box sx={{ mb: matchesMobile ? 1 : 0 }}>
                <Button
                  variant="contained"
                  style={{
                    width: matchesMobile ? '100%' : '160px',
                    height: '46px',
                    borderRadius: '24px',
                    textTransform: 'capitalize',
                    color: 'white',
                    backgroundColor: '#39A2DB',
                    borderColor: '#39A2DB',
                  }}
                  onClick={openCreateModal}
                  id="addNewOwnerBtn"
                >
                  Create New
                </Button>
              </Box>
            )}
          </Box>

          {String(WorkOrdersFetchError.statusCode)[0] === '4' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
                  <img src={BadRequestIcon} alt="400" />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {WorkOrdersFetchError.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Error
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {WorkOrdersFetchError.message.length === 0 ? '' : WorkOrdersFetchError.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : String(WorkOrdersFetchError.statusCode)[0] === '5' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={Error500} alt="500" style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {WorkOrdersFetchError.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {WorkOrdersFetchError.message.length === 0 ? '' : WorkOrdersFetchError.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : isFetchingWorkOrders ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : matchesMobile ? (
            <Box
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: 7,
                },
                '&::-webkit-scrollbar-track': {
                  boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                  borderRadius: '24px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'darkgrey',
                  borderRadius: '24px',
                },
                borderTop: '1px solid #EEEEEE',
              }}
            >
              {WorkOrdersDataPages?.[currentPage]?.Rows?.length ? (
                WorkOrdersDataPages?.[currentPage]?.Rows?.map((workorder) => {
                  return (
                    <Box key={workorder.Uuid} onClick={() => handleClickTableRow(workorder)} sx={{ borderBottom: '1px solid #EEEEEE', pt: 1, pb: 1.5 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '18px',
                          lineHeight: '22px',
                          color: '#14142B',
                        }}
                      >
                        {workorder?.Title || ''}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '15px',
                          lineHeight: '18px',
                          color: '#000000',
                        }}
                      >
                        {workorder?.Priority || ''}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '15px',
                          lineHeight: '18px',
                          color: '#000000',
                        }}
                      >
                        {workorder?.PropertyName || ''}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '15px',
                          lineHeight: '18px',
                          color: '#000000',
                        }}
                      >
                        {workorder?.Status || ''}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <></>
              )}
            </Box>
          ) : (
            <TableContainer
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: 7,
                },
                '&::-webkit-scrollbar-track': {
                  boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                  borderRadius: '24px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'darkgrey',
                  borderRadius: '24px',
                },
              }}
              style={{ flex: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {!matchesIpad && <TableCell id="subTitle">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.TypeId}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.TypeId)}
                        >
                          Type
                        </TableSortLabel>
                      </Stack>
                      </TableCell>}
                    {!matchesIpad && <TableCell id="subTitle">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.Id}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.Id)}
                        >
                          Work Order ID
                        </TableSortLabel>
                      </Stack>
                      </TableCell>}
                   
                    <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.Title}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.Title)}
                        >
                          Title
                        </TableSortLabel>
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.PropertyName}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.PropertyName)}
                        >
                           Property Name
                        </TableSortLabel>
                      </Stack>
                     </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.PriorityId}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.PriorityId)}
                        >
                         Priority
                        </TableSortLabel>
                        
                      </Stack>
                    </TableCell>
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.StatusId}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.StatusId)}
                        >
                         Status
                        </TableSortLabel>
                        
                      </Stack>
                    </TableCell>
                    {!matchesIpad && !isVendorUser && (
                      <TableCell id="subTitle">
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.AssignedTo}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.AssignedTo)}
                        >
                         Assigned to
                        </TableSortLabel>
                          
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell id="subTitle">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <TableSortLabel
                          active={sort.Column === WorkOrderSortColumnEnum.CreatedDate}
                          direction={sort.Direction === WorkOrderSortDirectionEnum.Asc ? 'asc' : 'desc'}
                           onClick={() => handleSort(WorkOrderSortColumnEnum.CreatedDate)}
                        >
                         Submitted Date
                        </TableSortLabel>
                        
                      </Stack>
                    </TableCell>
                    {!matchesIpad && (
                      <TableCell id="subTitle">
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          Details
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody style={{ left: '28px' }}>
                  {WorkOrdersDataPages?.[currentPage]?.Rows?.length ? (
                    WorkOrdersDataPages?.[currentPage]?.Rows?.map((workorder) => {
                      return (
                        <Fragment key={workorder.Uuid}>
                          <TableRow onClick={() => handleClickTableRow(workorder)}>
                            {!matchesIpad && <TableCell id="content">{workorder?.Type || ''}</TableCell>}
                            {!matchesIpad && <TableCell id="content">{workorder?.WorkOrderId || ''}</TableCell>}
                            <TableCell id="content">{workorder?.Title || ''}</TableCell>
                            <TableCell id="content">{workorder?.PropertyName || ''}</TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                {workorder?.Priority || ''}
                              </Stack>
                            </TableCell>
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} textAlign="center">
                                {workorder?.Status || ''}
                              </Stack>
                            </TableCell>
                            {!matchesIpad && !isVendorUser && (
                              <TableCell id="content">
                                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                  {workorder.Assignees?.length ? (
                                    <AvatarGroup max={5} className={classes.avatarGroupRoot}>
                                      {associatedCompanyStaff.staff
                                        ?.filter((pmStaff) => workorder.Assignees?.some((ws) => ws.Uuid === pmStaff.Uuid))
                                        .map((staff, index) => (
                                          <LetterAvatar name1={staff.FirstName} name2={staff.LastName} key={index} />
                                        ))}
                                      {Vendors?.Rows?.filter((pmVendor) => workorder.Assignees?.some((ws) => ws.Uuid === pmVendor.Uuid)).map((vendor, index) => (
                                        <LetterAvatar name1={vendor.Name ?? 'Vendor Name'} key={index} />
                                      ))}
                                    </AvatarGroup>
                                  ) : (
                                    <Avatar className={classes.avatarRoot} children="N/A" />
                                  )}
                                </Stack>
                              </TableCell>
                            )}
                            <TableCell id="content">
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                {workorder?.CreatedDate ? format(new Date(workorder.CreatedDate), 'MM/dd/yyyy') : ''}
                              </Stack>
                            </TableCell>
                            {!matchesIpad && (
                              <TableCell id="content">
                                <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                  <img width={13} height={14} src={ReviewOtpIcon} alt="details" style={{ cursor: 'pointer' }} onClick={() => handleClickWorkOrder(workorder)} />
                                </Stack>
                              </TableCell>
                            )}
                          </TableRow>
                        </Fragment>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 50, 100, 200]}
            count={WorkOrdersFetchResponseDto.TotalCount ?? 0}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />
        </Fragment>
      </Card>
      {/* Add new work order stuff */}
      {openCreateWorkOrderModal && <CreateWorkOrderModal open={openCreateWorkOrderModal} onClose={() => setOpenCreateWorkOrderModal(false)} />}
    </Grid>
  );
}
